<template>
    <div class="director">
        <index
            :payload-for-director-table="payloadForDirectorTable"
            :payload-for-pending-director-table="payloadForPendingDirectorTable"
            :payload-for-pending-update-particulars-of-directors-table="payloadForPendingUpdateParticularsOfDirectorsTable"
            @add="add"
            @view="view"
            @edit="edit"
            @remove="remove"
            @approve="approve"
            @viewUpdateOfParticulars="viewUpdateOfParticulars"
            @approveUpdateOfParticulars="approveUpdateOfParticulars"
        />

        <create-or-view-or-edit
            ref="createOrEdit"
            v-model="isDirectorSidebarActive"
            :data-status="status"
            :data-sidebar-action-loading="isSideBarActionLoading"
            :data-director="director"
            :data-auto-fill-requested-director="autoFillRequestedDirector"
            @add-director="create"
            @update-director="update"
            @request-autofill-director-data="requestAutofillDirectorData"
        />

        <create-or-view-particulars-of-director-form 
            ref="createOrViewParticularsOfDirectorForm"
            v-model="isGenerateDocumentSidebarActive"
            :data-to-view="director"
            :data-status="status"
            :data-sidebar-action-loading="isSideBarActionLoading"
        />

        <!-- delete -->
        <confirmation-modal  ref="modal">
            <template slot="body">
                <div>
                    <feather-icon class="d-block mx-auto text-warning" icon="AlertCircleIcon" size="90"/>
                    <h1 class="text-center text-secondary display-5 mt-2">Are you sure?</h1>
                    <p class="text-center mt-2"> You won't be able to revert this! </p>
                </div>
            </template>

            <template slot="footer">
                <b-button class="d-flex" :disabled="isSideBarActionLoading" size="md" variant="primary" @click="confirmDelete">
                    Yes, delete it!
                    <normal-loader v-if="isSideBarActionLoading" class="left-10%" />
                </b-button>

                <b-button size="md" variant="outline-danger" @click="cancelDelete">
                    Cancel
                </b-button>
            </template>
        </confirmation-modal>
    </div>
</template>

<script>
import {
    BButton,
} from "bootstrap-vue";
import { mapGetters } from "vuex";
import { ref } from '@vue/composition-api';
import { errorToast, successToast } from "@/views/helpers/toast";
import ConfirmationModal from '@/views/components/utils/ConfirmationModal.vue';
import Director from '@/models/Director';
import User from '@/models/User';
import GenerateDocument from '@/models/GenerateDocument';
import { 
    DIRECTOR_CREATE_SUCCESS, 
    DIRECTOR_DELETE_SUCCESS, 
    DIRECTOR_UPDATE_SUCCESS,
    DIRECTOR_APPROVE_SUCCESS,
} from '@/constants/success';
import { generateRandomUniqueId } from '@/helpers';
import { REFRESH_AND_TRY_AGAIN_ERROR, SOMETHING_WENT_WRONG } from '@/constants/errors';
import useDirectorsTable from './useDirectorsTable';
import usePendingApprovalDirectorsTable from './usePendingApprovalDirectorsTable';
import usePendingApprovalUpdateParticularsOfDirectorTable from './usePendingApprovalUpdateParticularsOfDirectorTable';
import CreateOrViewOrEdit from './CreateOrViewOrEdit.vue';
import CreateOrViewParticularsOfDirectorForm from '../../documents/prepare-documents/director-resolutions/CreateOrViewParticularsOfDirectorForm.vue';
import Index from './Index.vue';
import { CHANGE_OF_TYPES, COMPANY } from "@/constants/config";

export default {
    components: {
        Index,
        BButton,
        CreateOrViewOrEdit,
        CreateOrViewParticularsOfDirectorForm,
        ConfirmationModal,
    },

    data() {
        return {
            status: undefined,
            director: {},
            isDirectorSidebarActive: false,
            isGenerateDocumentSidebarActive: false,
            isSideBarActionLoading: false,
            autoFillRequestedDirector: {}
        };
    },

    computed: {
        ...mapGetters({
            isUserAdmin: "auth/isAdmin",
        }),
    },

    mounted()
    {
        // just to make sure the menu bar is shown
        this.$store.commit('appConfig/UPDATE_NAV_MENU_HIDDEN', false);
    },

    methods:{
        async requestAutofillDirectorData(id)
        {
            if (id) {
                const data = await User.getByNRIC(id);
                this.autoFillRequestedDirector = data;
            }
        },

        add(){
            this.director = {};
            this.status = "create";
            this.isDirectorSidebarActive = true;
        },

        view(data){
            this.director = data.item;
            this.status = "view";
            this.isDirectorSidebarActive = true;
        },

        viewUpdateOfParticulars(data){
            this.director = data;
            this.status = "view";
            this.isGenerateDocumentSidebarActive = true;
        },

        edit(data){
            this.director = data.item;
            this.status = "edit";
            this.isDirectorSidebarActive = true;
        }
    },

    setup() {
        const filterMode = ref(false);
        const dataToDelete = ref(undefined);
        // the list of these keys may not be same for even the same user(user_id)
        // the list will be ignored in frontend level update for the same user
        const IGNORE_KEYS_LIST = [
            "_id",
            "id",
            "appointmentDate",
            "cessationDate"
        ];

        const {
            sortPendingDirectorBy,
            isPendingDirectorBusy,
            pendingDirectorPerPage,
            PendingDirectorDataMeta,
            isLoadingPendingDirector,
            pendingDirectorSearchQuery,
            pendingDirectorCurrentPage,
            pendingDirectorTableColumns,
            isSortDescPendingDirector,
            totalPendingDirectors,
            pendingDirectorPerPageOptions,
            refPendingDirectorListTable,
            
            refetchPendingDirectorData,
            filterOnPendingDirector,
        } = usePendingApprovalDirectorsTable();

        const payloadForPendingDirectorTable = {
            sortPendingDirectorBy,
            isPendingDirectorBusy,
            pendingDirectorPerPage,
            PendingDirectorDataMeta,
            isLoadingPendingDirector,
            pendingDirectorSearchQuery,
            pendingDirectorCurrentPage,
            pendingDirectorTableColumns,
            isSortDescPendingDirector,
            totalPendingDirectors,
            pendingDirectorPerPageOptions,
            refPendingDirectorListTable,

            refetchPendingDirectorData,
            filterOnPendingDirector,
        };

        const {
            sortPendingApprovalUpdateParticularsOfDirectorBy,
            isPendingApprovalUpdateParticularsOfDirectorBusy,
            pendingApprovalUpdateParticularsOfDirectorPerPage,
            PendingApprovalUpdateParticularsOfDirectorDataMeta,
            isLoadingPendingApprovalUpdateParticularsOfDirector,
            pendingApprovalUpdateParticularsOfDirectorSearchQuery,
            pendingApprovalUpdateParticularsOfDirectorCurrentPage,
            pendingApprovalUpdateParticularsOfDirectorTableColumns,
            isSortDirDescPendingApprovalUpdateParticularsOfDirector,
            totalPendingApprovalUpdateParticularsOfDirectors,
            pendingApprovalUpdateParticularsOfDirectorPerPageOptions,
            refPendingApprovalUpdateParticularsOfDirectorListTable,

            pendingApprovalUpdateParticularsOfDirectors,
            refetchPendingApprovalUpdateParticularsOfDirectorData,
            filterOnPendingApprovalUpdateParticularsOfDirector,
        } = usePendingApprovalUpdateParticularsOfDirectorTable();

        const payloadForPendingUpdateParticularsOfDirectorsTable = {
            sortPendingApprovalUpdateParticularsOfDirectorBy,
            isPendingApprovalUpdateParticularsOfDirectorBusy,
            pendingApprovalUpdateParticularsOfDirectorPerPage,
            PendingApprovalUpdateParticularsOfDirectorDataMeta,
            isLoadingPendingApprovalUpdateParticularsOfDirector,
            pendingApprovalUpdateParticularsOfDirectorSearchQuery,
            pendingApprovalUpdateParticularsOfDirectorCurrentPage,
            pendingApprovalUpdateParticularsOfDirectorTableColumns,
            isSortDirDescPendingApprovalUpdateParticularsOfDirector,
            totalPendingApprovalUpdateParticularsOfDirectors,
            pendingApprovalUpdateParticularsOfDirectorPerPageOptions,
            refPendingApprovalUpdateParticularsOfDirectorListTable,

            pendingApprovalUpdateParticularsOfDirectors,
            refetchPendingApprovalUpdateParticularsOfDirectorData,
            filterOnPendingApprovalUpdateParticularsOfDirector,
        };

        const {
            // table data
            sortBy,
            perPage,
            isBusy,
            dataMeta,
            currentPage,
            searchQuery,
            tableColumns,
            isSortDirDesc,
            perPageOptions,

            // UI
            isLoading,

            directors,
            pendingDirectors,
            pendingUpdateOfDirectorsParticulars,
            refetchData,
            fetchDirectors,
            refDirectorListTable,
            totalDirectors
        } = useDirectorsTable();

        const payloadForDirectorTable = {
            sortBy,
            isBusy,
            perPage,
            dataMeta,
            directors,
            pendingDirectors,
            pendingUpdateOfDirectorsParticulars,
            isLoading,
            currentPage,
            refetchData,
            searchQuery,
            tableColumns,
            isSortDirDesc,
            perPageOptions,
            fetchDirectors,
            totalDirectors,
            refDirectorListTable,
        };

        function create(director) {
            if (this.isUserAdmin)
            {
                this.isSideBarActionLoading = true;

                // eslint-disable-next-line no-param-reassign
                director._id = generateRandomUniqueId();
                if(!director.cessationDate) {
                    // eslint-disable-next-line no-param-reassign
                    director.cessationDate = null;
                }

                Director.create(director).then(newDirector => {
                    this.isSideBarActionLoading = false;
                    this.isDirectorSidebarActive = false;

                    refDirectorListTable.value.localItems.push(director);
                    
                    // set the id from server to the new director
                    refDirectorListTable.value.localItems.forEach(item => {
                        if(item._id === director._id) {
                            // eslint-disable-next-line no-param-reassign
                            item.id = newDirector.id;
                            // eslint-disable-next-line no-param-reassign
                            item.userId = newDirector.user_id;
                        }
                    });
                    successToast(DIRECTOR_CREATE_SUCCESS);
                })
                    .catch(e => {
                        this.isSideBarActionLoading = false;
                        errorToast(SOMETHING_WENT_WRONG);
                        console.error(
                            "error while trying to create director",
                            e
                        );
                    });
            }
        };

        function update(data) {
            if (this.isUserAdmin)
            {
                this.isSideBarActionLoading = true;

                Director.update(data.id, data).then(() => {
                    this.isSideBarActionLoading = false;
                    this.isDirectorSidebarActive = false;

                    refDirectorListTable.value.localItems.forEach(item => {
                        if(item._id === data._id) {
                            Object.keys(data).forEach(key => {
                            // eslint-disable-next-line no-param-reassign
                                item[key] = data[key];
                            });
                        }

                        // update incase of same user id too
                        if(item.userId === data.userId) {
                            Object.keys(data).forEach(key => {
                                // update user related keys
                                if (! IGNORE_KEYS_LIST.includes(key))
                                {
                                    // eslint-disable-next-line no-param-reassign
                                    item[key] = data[key];
                                }
                            });
                        }
                    });
                    this.director = {};

                    successToast(DIRECTOR_UPDATE_SUCCESS);
                })
                    .catch(e => {
                        this.isSideBarActionLoading = false;
                        errorToast(SOMETHING_WENT_WRONG);
                        console.error(
                            "error while trying to update director",
                            e
                        );
                    });
            }
        };

        function remove(data) {
            this.$refs.modal.$refs.confirmationModal.show();
            dataToDelete.value= data;
        };

        function cancelDelete() {
            dataToDelete.value = null;
            this.$refs.modal.$refs.confirmationModal.hide();
        }

        function confirmDelete() {
            if (this.isUserAdmin) {
                this.isSideBarActionLoading = true;

                if (! dataToDelete.value.item.id)
                {
                    errorToast(REFRESH_AND_TRY_AGAIN_ERROR);
                    return;
                }

                Director.delete(dataToDelete.value.item.userId).then(() => {
                    refDirectorListTable.value.localItems = refDirectorListTable.value.localItems.filter(item => dataToDelete.value.item._id !== item._id);
                    this.$refs.modal.$refs.confirmationModal.hide();

                    this.isSideBarActionLoading = false;
                    dataToDelete.value = null;
                    successToast(DIRECTOR_DELETE_SUCCESS);
                })
                    .catch(e => {
                        this.isSideBarActionLoading = false;
                        errorToast(SOMETHING_WENT_WRONG);
                        console.error(
                            "error while trying to delete director",
                            e
                        );
                    });
            }
        };

        function approve(director) {
            if(this.isUserAdmin) {
                GenerateDocument.approvePendingDirector(director).then(approvedDirector => {
                    // appointment case
                    if(director.requestType === COMPANY.DIRECTOR.REQUEST_TYPE_APPOINT) {
                        refDirectorListTable.value.localItems.push(director);

                        // set the id from server to the approve director
                        refDirectorListTable.value.localItems.forEach(item => {
                            if(item._id === director._id) {
                            // eslint-disable-next-line no-param-reassign
                                item.id = approvedDirector.id;
                                // eslint-disable-next-line no-param-reassign
                                item.userId = approvedDirector.user_id;
                            }
                        });
                    }
                    // resign case 
                    else {
                        refDirectorListTable.value.localItems.forEach(item => {
                            if(item.userId === director.userId) {
                                // eslint-disable-next-line no-param-reassign
                                item.cessationDate = director.cessationDate;
                            }
                        });
                    }

                    // remove director from pending director table
                    refPendingDirectorListTable.value.localItems = refPendingDirectorListTable.value.localItems.filter(pendingDir => 
                        pendingDir._id !== director._id
                    );

                    /* 
                        # require to remove approved director from pending director array list too  
                        # to hide pending director table if pending director empty
                    */
                    pendingDirectors.value = pendingDirectors.value.filter(pendingDirector =>
                        pendingDirector._id !== director._id
                    );

                    successToast(DIRECTOR_APPROVE_SUCCESS);
                })
                    .catch(e => {
                        if(e.response?.data?.message) {
                            errorToast(e.response?.data?.message);
                                
                        } else {
                            errorToast(SOMETHING_WENT_WRONG);
                            console.error(
                                "error while trying to approve pending director",
                                e
                            );
                        }
                    });
            }
        };
        
        /* eslint-disable no-param-reassign */
        function approveUpdateOfParticulars(data) {
            GenerateDocument.approveParticularsOfDirectorForm(data)
                .then(() => {
                    refDirectorListTable.value.localItems.forEach(item => {
                        if(item.userId === data.director.userId) {
                            // since particular of nric change might be different id type 
                            // and update email particular totally change new user in system level 
                            // the better safe approach is fetch shareholder instant of every particular find and update
                            const requireReFetchDirector =  data.updatedDirectorParticularList.filter(
                                particular => 
                                    particular.changeOfType.toLowerCase() === CHANGE_OF_TYPES.PASSPORT.toLowerCase() || 
                                    particular.changeOfType.toLowerCase() === CHANGE_OF_TYPES.EMAIL.toLowerCase()
                            ).length > 0;

                            if(requireReFetchDirector) {
                                refDirectorListTable.value.localItems = [];
                                fetchDirectors();
                            } else {
                                data.updatedDirectorParticularList.forEach(list => {
                                    if(list.changeOfType.toLowerCase() === CHANGE_OF_TYPES.NAME.toLowerCase()) {
                                        item.name = list.changeTo;
                                    } else if(list.changeOfType.toLowerCase() === CHANGE_OF_TYPES.CONTACT.toLowerCase()) {
                                        item.contactNumber = list.changeTo;
                                    } else if(list.changeOfType.toLowerCase() === CHANGE_OF_TYPES.ADDRESS.toLowerCase()) {
                                        item.address = list.changeTo;
                                    } else if(list.changeOfType.toLowerCase() === CHANGE_OF_TYPES.ALTERNATIVE_ADDRESS.toLowerCase()) {
                                        item.address = list.changeTo;
                                    }
                                });
                            }
                         
                            refPendingApprovalUpdateParticularsOfDirectorListTable.value.localItems = refPendingApprovalUpdateParticularsOfDirectorListTable.value.localItems.filter(pendingUpdateParticularsOfDirector => 
                                pendingUpdateParticularsOfDirector.companyProcessId !== data.companyProcessId
                            );
                           
                            pendingUpdateOfDirectorsParticulars.value = pendingUpdateOfDirectorsParticulars.value.filter(pendingApprovalUpdateParticularsOfDirector =>
                                pendingApprovalUpdateParticularsOfDirector.companyProcessId !== data.companyProcessId
                            );

                            refDirectorListTable.value.localItems = [];
                            fetchDirectors();

                            successToast(`The update of particulars have been approved successfully.`);
                        }
                    });
                })
                .catch(error => {
                    if(error.response?.data?.message) {
                        errorToast(error.response?.data?.message);
                                
                    } else {
                        errorToast(SOMETHING_WENT_WRONG);
                        console.error(
                            "error while trying to approve particulars of director form",
                            error
                        );
                    }
                });
        };
        /* eslint-enable no-param-reassign */

        return {
            refDirectorListTable,
            refPendingDirectorListTable,
            pendingDirectors,
            pendingUpdateOfDirectorsParticulars,
            refetchData,
            fetchDirectors,

            // UI
            filterMode,

            // methods
            create,
            update,
            remove,
            confirmDelete,
            cancelDelete,
            approve,
            approveUpdateOfParticulars,
            payloadForDirectorTable,
            payloadForPendingDirectorTable,
            payloadForPendingUpdateParticularsOfDirectorsTable,
        };
    }
};
</script>
